import { FC, useMemo, useState } from 'react';
import {
    StyledAccordion,
    StyledEstablishmentFeeCollapsedSummary,
    StyledEstablishmentFeeSummary,
    StyledEstablishmentFeeSummaryGrid,
    StyledEstablishmentFeeSummaryLineItemGrid,
    StyledInclServiceDisclaimer,
    StyledLineItem,
    StyledLineItemValue,
    StyledMonthlyAndTotalGrid,
    StyledTotalInPeriod,
    StyledTotalMonthly,
} from '../selected-leasing-summary-modal.styled';
import { Abbreviations } from '../../../../constants/units-and-abbreviations';
import { calculateHireSelectedStartupPrice, calculateHireSelectedTotalPrice, formatPrice } from '../../../../utils/helpers';
import { StartupCostItem } from '../startup-cost-item/startup-cost-item.component';
import { ProductDetailsStore } from '../../../../lib/state/hessel-site/product-details';
import { SelectedLeasingStartupCostContent } from '../selected-leasing-summary-modal';
import { MiniBasketStore } from '../../../../lib/state/hessel-site/mini-basket';

type IProps = {
    startupCostContent?: SelectedLeasingStartupCostContent;
    hideMonthlyPrice?: boolean;
    className?: string;
    accordionBackground?: string;
    useBasketProductDetails?: boolean;
};

export const SelectedTotalSummary: FC<IProps> = ({
    startupCostContent,
    className,
    hideMonthlyPrice = false,
    accordionBackground,
    useBasketProductDetails,
}) => {
    const { selectedProduct: selectedProductDetailsStore, optionalEquipmentPriceSum } = ProductDetailsStore.useStoreState((state) => state);
    const { productData } = MiniBasketStore.useStoreState((state) => state);
    const selectedProduct = useBasketProductDetails && productData?.selectedProduct ? productData?.selectedProduct : selectedProductDetailsStore;

    const selectedEquipment = useMemo(() => {
        return selectedProduct?.optionalEquipment['HiRE Selected']?.filter((item) => item.isSelected);
    }, [selectedProduct?.optionalEquipment]);
    const [establishmentFeeSummaryOpen, setEstablishmentFeeSummaryOpen] = useState(false);
    const selectedInsurance = useMemo(() => {
        return selectedEquipment?.find((item) => item.types?.includes('Forsikring'));
    }, [selectedEquipment]);

    const startupTotalPrice = useMemo(() => {
        if (!selectedProduct?.purchaseTypes.hireSelected) return 0;
        const price = calculateHireSelectedStartupPrice(selectedProduct?.purchaseTypes.hireSelected, selectedProduct.optionalEquipment);
        return price;
    }, [selectedProduct]);

    const totalPrice = useMemo(() => {
        if (!selectedProduct?.purchaseTypes.hireSelected) return 0;
        const price = calculateHireSelectedTotalPrice(
            selectedProduct?.purchaseTypes.hireSelected,
            selectedProduct.optionalEquipment,
            optionalEquipmentPriceSum
        );
        return price;
    }, [optionalEquipmentPriceSum, selectedProduct]);

    if (!selectedProduct) return null;

    return (
        <StyledMonthlyAndTotalGrid className={className}>
            {!hideMonthlyPrice && (
                <>
                    <StyledTotalMonthly>
                        <span>Månedlig ydelse</span>
                        <span>
                            {formatPrice(selectedProduct.purchaseTypes.hireSelected.baseMonthlyPrice)} {Abbreviations.KR_SLASH_MD}
                        </span>
                    </StyledTotalMonthly>
                    <StyledInclServiceDisclaimer>Inkl. service</StyledInclServiceDisclaimer>
                </>
            )}
            <StyledTotalInPeriod>
                <span>{`Totalomkostninger i ${selectedProduct.purchaseTypes.hireSelected.durationMonths} ${Abbreviations.MDR}`}</span>
                <span>{`${formatPrice(totalPrice)} ${Abbreviations.KR}`}</span>
            </StyledTotalInPeriod>
            <StyledEstablishmentFeeSummaryGrid>
                <StyledEstablishmentFeeCollapsedSummary hide={establishmentFeeSummaryOpen}>
                    {formatPrice(startupTotalPrice)} {Abbreviations.KR}
                </StyledEstablishmentFeeCollapsedSummary>

                <StyledAccordion
                    title={'Heraf opstartsudgifter'}
                    onClick={() => setEstablishmentFeeSummaryOpen(!establishmentFeeSummaryOpen)}
                    isOpen={establishmentFeeSummaryOpen}
                    index={0}
                    variant="primary"
                    border="none"
                    customTitle={<StyledEstablishmentFeeSummary>Heraf opstartsudgifter</StyledEstablishmentFeeSummary>}
                    backgroundColor={accordionBackground}
                >
                    <StyledEstablishmentFeeSummaryLineItemGrid>
                        <StartupCostItem
                            price={selectedProduct.purchaseTypes.hireSelected?.initialPayment}
                            label={startupCostContent?.initialPaymentLabel}
                            tooltip={startupCostContent?.initialPaymentTooltip}
                        />
                        {!selectedInsurance && (
                            <StartupCostItem
                                price={selectedProduct.purchaseTypes.hireSelected?.insuranceOptOutFee}
                                label={startupCostContent?.insuranceOptOutFeeLabel}
                                tooltip={startupCostContent?.insuranceOptOutFeeTooltip}
                            />
                        )}
                        <StartupCostItem
                            price={selectedProduct.purchaseTypes.hireSelected?.establishmentFee}
                            label={startupCostContent?.establishmentFeeLabel}
                            tooltip={startupCostContent?.establishmentFeeTooltip}
                        />
                        <StartupCostItem
                            price={selectedProduct.purchaseTypes.hireSelected?.returnFee}
                            label={startupCostContent?.returnFeeLabel}
                            tooltip={startupCostContent?.returnFeeTooltip}
                        />
                        <StyledLineItem>
                            <StyledLineItemValue>I alt ved opstart</StyledLineItemValue>
                            <span>
                                {formatPrice(startupTotalPrice)} {Abbreviations.KR}
                            </span>
                        </StyledLineItem>
                    </StyledEstablishmentFeeSummaryLineItemGrid>
                </StyledAccordion>
            </StyledEstablishmentFeeSummaryGrid>
        </StyledMonthlyAndTotalGrid>
    );
};
